import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Calendar, ChartLine, LoaderCircle, Search, Users } from 'lucide-react'

import { getEnterpriseTeams, type GetMembersTableWordsWindowEnum } from '@/api/teamMembersTable'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import FilterUpgradeTooltip from '@/pages/app/UsageV2/filters/FilterUpgradeTooltip'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

// Sentinel for the "all" select option. Radix Select rejects ``value=""``,
// so the dropdowns use a non-empty token and translate it back to ``undefined``
// at the boundary.
const ALL = '__all__'

type StatusValue = 'active' | 'trialing'

// Display labels for the words-window picker. ``all_time`` is the default and
// surfaces as the table's lifetime "# of words" column.
const WORDS_WINDOW_OPTIONS: Array<{ value: GetMembersTableWordsWindowEnum; label: string }> = [
  { value: 'all_time', label: 'All time' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
]

interface TeamMembersFiltersProps {
  search: string
  onSearchChange: (next: string) => void
  status: StatusValue | undefined
  onStatusChange: (next: StatusValue | undefined) => void
  teamId: string | undefined
  onTeamIdChange: (next: string | undefined) => void
  wordsWindow: GetMembersTableWordsWindowEnum
  onWordsWindowChange: (next: GetMembersTableWordsWindowEnum) => void
  isFetching: boolean
  upgradeLocked?: boolean
}

const TeamMembersFilters = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  teamId,
  onTeamIdChange,
  wordsWindow,
  onWordsWindowChange,
  isFetching,
  upgradeLocked = false,
}: TeamMembersFiltersProps) => {
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseId = enterprise?.id ?? null

  // Teams power the Department dropdown. Loaded once and cached — admins
  // rarely create teams during a single Insights session.
  const teamsQuery = useQuery({
    queryKey: ['team-members', 'teams', enterpriseId],
    queryFn: () => getEnterpriseTeams(enterpriseId!).then((r) => r.data.teams),
    enabled: !!enterpriseId && !upgradeLocked,
    staleTime: 5 * 60 * 1000,
  })

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterUpgradeTooltip active={upgradeLocked}>
        <Select
          value={wordsWindow}
          onValueChange={(v) => onWordsWindowChange(v as GetMembersTableWordsWindowEnum)}
          disabled={upgradeLocked}
        >
          <SelectTrigger className="data-[size=default]:h-10 w-auto cursor-pointer px-4" aria-label="Words window">
            <Calendar className="text-foreground size-[22px]" aria-hidden="true" />
            <SelectValue>{WORDS_WINDOW_OPTIONS.find((o) => o.value === wordsWindow)?.label} (# of words)</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {WORDS_WINDOW_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterUpgradeTooltip>

      <FilterUpgradeTooltip active={upgradeLocked}>
        <Select
          value={status ?? ALL}
          onValueChange={(v) => onStatusChange(v === ALL ? undefined : (v as StatusValue))}
          disabled={upgradeLocked}
        >
          <SelectTrigger className="data-[size=default]:h-10 w-auto cursor-pointer px-4">
            <ChartLine className="text-foreground size-[22px]" aria-hidden="true" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
          </SelectContent>
        </Select>
      </FilterUpgradeTooltip>

      <FilterUpgradeTooltip active={upgradeLocked}>
        <Select
          value={teamId ?? ALL}
          onValueChange={(v) => onTeamIdChange(v === ALL ? undefined : v)}
          disabled={upgradeLocked}
        >
          <SelectTrigger className="data-[size=default]:h-10 w-auto cursor-pointer px-4">
            <Users className="text-foreground size-[22px]" aria-hidden="true" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All departments</SelectItem>
            {teamsQuery.data?.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterUpgradeTooltip>

      <FilterUpgradeTooltip active={upgradeLocked}>
        <div className="relative w-[311px]">
          <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search team members"
            className="h-10 pl-9 pr-9"
            aria-label="Search team members"
            disabled={upgradeLocked}
          />
          {isFetching && (
            <LoaderCircle
              className="text-muted-foreground pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin"
              aria-hidden="true"
            />
          )}
        </div>
      </FilterUpgradeTooltip>
    </div>
  )
}

export default TeamMembersFilters
